import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Sparkles, Download, Printer } from "lucide-react";
import { getPnl, monthLabel, CATEGORY_LABEL, EXPENSE_CATEGORIES } from "@/lib/finance";
import { formatInr } from "@/lib/format";
import { errorMessage } from "@/lib/api";

const REV_LABEL = { SUBSCRIPTION: "Subscription", COUPON_TOPUP: "Coupon Top-up", MOVIE_COMMISSION: "Movie Comm.", EVENT_PROMOTION_FEE: "Events", MALL_AD_CONTRACT: "Mall Ads", AFFILIATE_CLICK_PAYOUT: "Affiliate", OTHER: "Other" };
const COLORS = ["#f97316", "#0ea5e9", "#10b981", "#a855f7", "#eab308", "#ef4444", "#6366f1", "#78716c"];

export default function PnlPage() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getPnl(year, month).then((d) => { setData(d); setLoading(false); }).catch((e) => { toast.error(errorMessage(e)); setLoading(false); });
    }, [year, month]);

    const revData = useMemo(() => data ? Object.entries(data.revenue.by_type).map(([k, v]) => ({ name: REV_LABEL[k] || k, value: v / 100 })) : [], [data]);
    const expData = useMemo(() => data ? EXPENSE_CATEGORIES.map((c) => ({ name: c.label, value: (data.expenses.by_category[c.value] || 0) / 100 })) : [], [data]);
    const daily = useMemo(() => data ? (data.daily_series || []).map((d) => ({ name: d.date.slice(8), Revenue: d.revenue_paise / 100, Expenses: d.expenses_paise / 100, Net: d.net_paise / 100 })) : [], [data]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [["date", "revenue_paise", "expenses_paise", "net_paise"]];
        data.daily_series.forEach((d) => rows.push([d.date, d.revenue_paise, d.expenses_paise, d.net_paise]));
        const csv = rows.map((r) => r.join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a"); a.href = url; a.download = `pnl-${year}-${month}.csv`; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="pnl-page">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-orange-600">Finance · Reports</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Profit &amp; Loss</h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                        <SelectTrigger className="w-28" data-testid="pnl-month"><SelectValue /></SelectTrigger>
                        <SelectContent>{Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{new Date(2000, i, 1).toLocaleDateString("en", { month: "long" })}</SelectItem>
                        ))}</SelectContent>
                    </Select>
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                        <SelectTrigger className="w-24" data-testid="pnl-year"><SelectValue /></SelectTrigger>
                        <SelectContent>{[year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                    <Link to={`/admin/finance/ai-accountant?action=monthly-summary&year=${year}&month=${month}`}>
                        <Button variant="outline" data-testid="pnl-ai-report"><Sparkles size={14} className="mr-1" /> Generate AI Report</Button>
                    </Link>
                    <Button variant="outline" onClick={exportCsv} data-testid="pnl-export"><Download size={14} className="mr-1" /> CSV</Button>
                    <Button variant="outline" onClick={() => window.print()} data-testid="pnl-print"><Printer size={14} className="mr-1" /> Print</Button>
                </div>
            </div>

            {loading ? <Skeleton className="h-40 rounded-2xl" /> : data && (
                <Card className={`mb-4 rounded-2xl border-0 bg-gradient-to-br p-6 text-white ${data.net_profit_paise >= 0 ? "from-emerald-600 to-emerald-400" : "from-rose-700 to-rose-500"}`} data-testid="pnl-hero">
                    <div className="text-xs uppercase opacity-90">Net Profit · {monthLabel(year, month)}</div>
                    <div className="mt-1 text-4xl font-extrabold">{formatInr(data.net_profit_paise)}</div>
                    <div className="mt-1 text-sm opacity-90">
                        {data.margin_percent == null ? "—" : `${data.margin_percent}% margin`} · Revenue {formatInr(data.revenue.total_paise)} · Expenses {formatInr(data.expenses.total_paise)}
                    </div>
                </Card>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <div className="mb-2 text-sm font-bold">Revenue by type</div>
                    <div className="h-64">{loading ? <Skeleton className="h-full" /> : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={revData.filter((r) => r.value > 0)} dataKey="value" outerRadius={80} label={(d) => d.name}>
                                    {revData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip formatter={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}</div>
                </Card>
                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <div className="mb-2 text-sm font-bold">Expenses by category</div>
                    <div className="h-64">{loading ? <Skeleton className="h-full" /> : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={expData.filter((r) => r.value > 0)} dataKey="value" outerRadius={80} label={(d) => d.name}>
                                    {expData.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                                </Pie>
                                <Tooltip formatter={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}</div>
                    <ul className="mt-2 space-y-0.5 text-[11px]" data-testid="exp-cat-list">
                        {expData.map((c) => (
                            <li key={c.name} className="flex justify-between"><span>{c.name}</span><span className="font-mono">{formatInr(c.value * 100)}</span></li>
                        ))}
                    </ul>
                </Card>
            </div>

            <Card className="mt-4 rounded-2xl border border-neutral-200 p-4">
                <div className="mb-2 text-sm font-bold">Daily flow</div>
                <div className="h-64">{loading ? <Skeleton className="h-full" /> : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={daily}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="name" fontSize={11} />
                            <YAxis fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />
                            <Legend />
                            <Bar dataKey="Revenue" fill="#f97316" />
                            <Bar dataKey="Expenses" fill="#e11d48" />
                            <Bar dataKey="Net" fill="#059669" />
                        </BarChart>
                    </ResponsiveContainer>
                )}</div>
            </Card>
        </div>
    );
}
